/**
 * invariants.ts
 *
 * Type definitions for invariants - rules that enforce:
 * 1. Normalization (state consistency)
 * 2. Permissions (who can execute which commands)
 * 3. Validation (data integrity for UI feedback)
 *
 * Invariants are checked BEFORE and AFTER command execution.
 */

import type { CapabilityKey } from '@bett3r-dev/teselly-capabilities';
import { isAdminOperation, operationCapability } from '@bett3r-dev/teselly-capabilities';
import { serverClientOperations, type ServerClientCommandName } from '@bett3r-dev/teselly-client-library';
import { can } from '../../../hooks/useCapability';
import type { User } from '../../../contexts/useAuth/useAuth';
import { Command, CommandMeta } from './types';

/**
 * Invariants define the rules for state consistency and permissions.
 *
 * Three optional functions:
 * - normalize: Enforce state consistency after mutations
 * - canApply: Permission control (who can execute what)
 * - validate: Data validation for UI error display
 */
export type Invariants<TState, TCommand extends Command> = {
  /**
   * Normalize state after commands are applied.
   * Use this to enforce consistency and set defaults.
   *
   * Examples:
   * - Ensure activeStep exists in valid steps array
   * - Set default values for missing fields
   * - Derive computed fields
   *
   * MUST return a new state object (immutable).
   * MUST be deterministic (same input = same output).
   *
   * @example
   * ```typescript
   * normalize: (state) => {
   *   // Ensure activeStep is valid
   *   const validSteps = ['step1', 'step2', 'review'];
   *   if (!validSteps.includes(state.activeStep)) {
   *     return { ...state, activeStep: 'step1' };
   *   }
   *   return state;
   * }
   * ```
   */
  normalize?: ( state: TState ) => TState;

  /**
   * Check if a source can execute a command (permissions).
   * Return false to block the command.
   *
   * Permission patterns:
   * - 'api' source can only modify `options.*` (fetched data)
   * - 'url' source can only modify navigation/filters
   * - 'ai' source cannot modify API-controlled data
   * - 'user' source has broad permissions
   *
   * Called BEFORE the command is applied.
   *
   * @example
   * ```typescript
   * canApply: ({ command, meta }) => {
   *   // API source can only set options
   *   if (meta.source === 'api') {
   *     return command.type === 'wizard/options/setPlans';
   *   }
   *
   *   // URL source can only navigate
   *   if (meta.source === 'url') {
   *     return command.type === 'wizard/navigation/goToStep';
   *   }
   *
   *   // User and AI can do most things
   *   return true;
   * }
   * ```
   */
  canApply?: ( args: {
    state: TState;
    command: TCommand;
    meta: CommandMeta;
  }) => boolean;

  /**
   * Validate state and return errors for UI display.
   * Return null if state is valid.
   *
   * Used for:
   * - Form validation
   * - Required field checks
   * - Business rule validation
   *
   * Called AFTER normalize, on every state change.
   *
   * @example
   * ```typescript
   * validate: (state) => {
   *   const errors: Record<string, string> = {};
   *
   *   // Only validate current step
   *   if (state.activeStep === 'step2' && !state.data.country) {
   *     errors['country'] = 'Country is required';
   *   }
   *
   *   if (state.activeStep === 'step2' && !state.data.planId) {
   *     errors['plan'] = 'Please select a plan';
   *   }
   *
   *   return Object.keys(errors).length > 0 ? errors : null;
   * }
   * ```
   */
  validate?: ( state: TState ) => Record<string, string> | null;
};

/**
 * `capabilityRequired` composes with `canApply` to enforce FE capability gating.
 *
 * Returns a predicate that:
 * - Bypasses the check when `meta.source !== 'user'` ( SDD §7.3 K10 — API / AI /
 *   URL sources are already authorized on the BE; FE re-checks would create
 *   false-positive blocks for programmatic dispatches ).
 * - Otherwise delegates to the isomorphic `can( user, key, instance )` from
 *   `useCapability`. When `getInstance` is provided, the result of calling it
 *   with `( state, command )` is passed as the instance for per-instance
 *   gating; otherwise the check is "has any scope for this capability".
 *
 * The authenticated user is read from `meta.user`. Dispatchers are responsible
 * for attaching it on user-sourced commands ( typically from the auth context
 * at the dispatch site ).
 *
 * @example
 * ```typescript
 * canApply: ( args ) =>
 *   capabilityRequired(
 *     'items-publishing:publications:update',
 *     ( s, c ) => s.publications[c.publicationId]
 *   )( args ) && existingChecks( args );
 * ```
 */
export function capabilityRequired<TState, TCommand extends Command>(
  key: CapabilityKey,
  getInstance?: ( state: TState, command: TCommand ) => Record<string, unknown> | undefined
): ( args: { state: TState; command: TCommand; meta: CommandMeta }) => boolean {
  return ({ state, command, meta }) => {
    if ( meta.source !== 'user' ) return true;
    const user = ( meta as CommandMeta & { user?: User | null }).user ?? null;
    const instance = getInstance?.( state, command );
    return can( user, key, instance );
  };
}

/**
 * Resolves the capability a command requires. Either a static key, or a
 * function that derives it from state/command for cases where one command
 * maps to different capabilities depending on context ( e.g. a shared
 * `form/submit` that is create-while-adding, update-while-editing ).
 * Returning `undefined` means the command is ungated.
 */
export type CapabilityGate<TState, TCommand extends Command> =
  | CapabilityKey
  | (( state: TState, command: TCommand ) => CapabilityKey | undefined );

/**
 * Declarative, co-located map of command type → required capability for
 * user-sourced dispatches. This is the SINGLE source of truth for FE
 * capability gating: it is consumed both at runtime ( the canApply
 * composition helper ) and statically by the capabilities.html generator,
 * which reads this object literal directly — so the runtime gate and the
 * generated authorization doc can never drift.
 *
 * Keys are exact command types ( `Partial<Record<TCommand['type'], …>>`
 * ties them to the store's command union, so a renamed command is a
 * compile error here ). Only user-sourced commands are gated; api / ai /
 * url sources are authorized upstream.
 */
export type CapabilityGateMap<TState, TCommand extends Command> =
  Partial<Record<TCommand['type'], CapabilityGate<TState, TCommand>>>;

/**
 * Runtime counterpart to a `CapabilityGateMap`: resolves the gate for the
 * dispatched command and authorizes it via the isomorphic `can()`. Compose
 * this into a store's `canApply` user branch:
 *
 * ```typescript
 * if ( source === 'user' ) {
 *   if ( type.startsWith( 'warehouses/api/' )) return false;
 *   return gateCommandByCapability( capabilityGates, { state, command, meta });
 * }
 * ```
 *
 * Non-user sources pass through ( authorized upstream ); a command with no
 * gate passes through. The acting user is read from `meta.user`, attached at
 * the dispatch boundary. This is a coarse "has any scope for this capability"
 * check — pass per-instance gating through `capabilityRequired` where needed.
 */
export function gateCommandByCapability<TState, TCommand extends Command>(
  gates: CapabilityGateMap<TState, TCommand>,
  args: { state: TState; command: TCommand; meta: CommandMeta }
): boolean {
  const { state, command, meta } = args;
  if ( meta.source !== 'user' ) return true;
  const gate = ( gates as Record<string, CapabilityGate<TState, TCommand>> )[ command.type ];
  if ( gate === undefined ) return true;
  const key = typeof gate === 'function' ? gate( state, command ) : gate;
  if ( !key ) return true;
  const user = ( meta.user ?? null ) as User | null;
  return can( user, key );
}

/**
 * The authorization target a command maps to. Either a typed client-command
 * NAME ( from `serverClientOperations` ) whose backend operation owns the
 * required capability, or the explicit `{ public: true }` sentinel marking the
 * command as deliberately ungated. There is no "unmarked gap": a command is
 * either absent from the map ( not gated by this helper ), gated by a named
 * operation, or explicitly public.
 */
export type GateTarget = ServerClientCommandName | { readonly public: true };

/**
 * Declarative, co-located map of command type → authorization target for
 * user-sourced dispatches, anchored on the backend OPERATION the command's
 * policy invokes rather than a hand-typed capability string. The capability is
 * derived from the named client method ( `serverClientOperations[name].operation`
 * → `operationCapability` ), so re-bucketing an operation's capability on the
 * backend reflows every FE gate via regeneration with no hand-edit.
 *
 * Mirrors `CapabilityGateMap`: keys are exact command types
 * ( `Partial<Record<TCommand['type'], …>>` ties them to the store's command
 * union, so a renamed command is a compile error ) and each entry is either a
 * static `GateTarget` or a `( state, command ) => GateTarget` for commands that
 * resolve to different operations by context ( e.g. a shared `form/submit` that
 * is create-while-adding, update-while-editing ).
 */
export type CommandOperationMap<TState, TCommand extends Command> =
  Partial<Record<TCommand['type'], GateTarget | (( state: TState, command: TCommand ) => GateTarget )>>;

/**
 * Shared resolution of a `CommandOperationMap` entry to a concrete
 * `GateTarget`: looks up the dispatched command's entry and, when it is the
 * `( state, command ) => GateTarget` function form, evaluates it against live
 * state. Returns `undefined` when the command is absent from the map.
 *
 * Factored out so the gate ( `gateCommandByOperation` ) and the call-site
 * method selector ( `resolveCommandName` ) resolve the target identically and
 * can never diverge.
 */
function resolveGateTarget<TState, TCommand extends Command>(
  map: CommandOperationMap<TState, TCommand>,
  command: TCommand,
  state: TState
): GateTarget | undefined {
  const entry = ( map as Record<string, GateTarget | (( state: TState, command: TCommand ) => GateTarget )> )[ command.type ];
  if ( entry === undefined ) return undefined;
  return typeof entry === 'function' ? entry( state, command ) : entry;
}

/**
 * Resolves the `ServerClientCommandName` a command maps to, for the policy
 * call site to select `backend.commands[ name ]`. Shares
 * `resolveGateTarget` with `gateCommandByOperation`, so the GATED operation and
 * the INVOKED operation derive from one declaration ( the entry in
 * `commandOperations` ).
 *
 * Returns `undefined` when the command is absent from the map or its entry is
 * the `{ public: true }` sentinel ( no named server command to invoke ).
 */
export function resolveCommandName<TState, TCommand extends Command>(
  map: CommandOperationMap<TState, TCommand>,
  command: TCommand,
  state: TState
): ServerClientCommandName | undefined {
  const target = resolveGateTarget( map, command, state );
  if ( target === undefined ) return undefined;
  if ( typeof target === 'object' ) return undefined;
  return target;
}

/**
 * Runtime counterpart to a `CommandOperationMap`: resolves the gate target for
 * the dispatched command, derives the required capability from the named
 * operation, and authorizes it via the isomorphic `can()`. Compose this into a
 * store's `canApply` user branch ( mirrors `gateCommandByCapability` ).
 *
 * - Non-user sources pass through ( authorized upstream ).
 * - A command absent from the map passes through ( not gated by THIS helper —
 *   the coverage CI check is what forbids a privileged command from being
 *   silently absent ).
 * - The `{ public: true }` sentinel passes through ( explicitly, deliberately
 *   ungated ).
 * - Otherwise the target is a `ServerClientCommandName`; the capability is
 *   `operationCapability[ serverClientOperations[ target ].operation ]`. A
 *   referenced operation with no capability throws ( no silent pass ).
 *
 * When `getInstance` is provided, its result is passed to `can()` as the
 * per-instance for instance-level gating. The acting user is read from
 * `meta.user`, attached at the dispatch boundary.
 */
export function gateCommandByOperation<TState, TCommand extends Command>(
  map: CommandOperationMap<TState, TCommand>,
  args: { state: TState; command: TCommand; meta: CommandMeta },
  getInstance?: ( state: TState, command: TCommand ) => Record<string, unknown> | undefined
): boolean {
  const { state, command, meta } = args;
  if ( meta.source !== 'user' ) return true;
  const target = resolveGateTarget( map, command, state );
  if ( target === undefined ) return true;
  if ( typeof target === 'object' ) return true;
  const opMeta = serverClientOperations[ target ];
  // `public` / `internal` operations omit `operation` from their metadata, so
  // the property is only present on `private` entries — narrow before reading.
  const op = 'operation' in opMeta ? opMeta.operation : undefined;
  // Admin-only / internal private routes carry a `default:<op>` identity that owns
  // NO org capability by design; they are gated ADMIN-ONLY at the gateway (Step 5b),
  // which is authoritative. Pass through here — semantically identical to the
  // `{ public: true }` sentinel: not gated by an ORG capability at the FE. The FE
  // `User` has no admin/role field, so a real FE-side admin re-check is impossible
  // (TV1-2051). Resolved BEFORE the capability lookup so it never spuriously throws.
  if ( isAdminOperation( op )) return true;
  const cap = op === undefined ? undefined : operationCapability[ op ];
  if ( cap === undefined ) {
    throw new Error( `Operation ${op} has no capability and is not marked public` );
  }
  const user = ( meta.user ?? null ) as User | null;
  return can( user, cap, getInstance?.( state, command ));
}
